import { UUIDatePickerTheme } from '../../../types/UUIDatePicker'

const sizeClasses = {
  small: {
    input: 'tw:h-6 tw:text-xs tw:px-2',
    panel: 'tw:text-xs',
    header: 'tw:px-2 tw:py-1.5',
    headerButton: 'tw:w-6 tw:h-5',
    selectTrigger: 'tw:h-6 tw:pl-3 tw:pr-2 tw:text-xs',
    monthSelectTrigger: 'tw:h-6 tw:pl-3 tw:pr-2 tw:text-xs tw:w-[8.5em]',
    yearSelectTrigger: 'tw:h-6 tw:pl-3 tw:pr-2 tw:text-xs tw:w-[5.5em]',
    selectOptions: 'tw:text-xs',
    selectOption: 'tw:pl-3 tw:pr-2 tw:py-1',
    calendar: 'tw:p-2',
    weekday: 'tw:min-w-[1.75em] tw:h-[1.75em] tw:text-[10px]',
    day: 'tw:min-w-[1.75em] tw:h-[1.75em] tw:text-xs',
    footer: 'tw:px-2 tw:py-1.5',
    applyButton: 'tw:h-6 tw:px-2 tw:text-xs',
    clearButton: 'tw:w-4 tw:h-4',
    icon: 'tw:[&_[data-uuidatepicker-icon]]:text-[14px]'
  },
  normal: {
    input: 'tw:h-8 tw:text-sm tw:px-3',
    panel: 'tw:text-sm',
    header: 'tw:px-3 tw:py-2',
    headerButton: 'tw:w-8 tw:h-7',
    selectTrigger: 'tw:h-7 tw:pl-4 tw:pr-3 tw:text-sm',
    monthSelectTrigger: 'tw:h-7 tw:pl-4 tw:pr-3 tw:text-sm tw:w-[9em]',
    yearSelectTrigger: 'tw:h-7 tw:pl-4 tw:pr-3 tw:text-sm tw:w-[5.5em]',
    selectOptions: 'tw:text-sm',
    selectOption: 'tw:pl-4 tw:pr-3 tw:py-1.5',
    calendar: 'tw:p-3',
    weekday: 'tw:min-w-[2.25em] tw:h-[2.25em] tw:text-xs',
    day: 'tw:min-w-[2.25em] tw:h-[2.25em] tw:text-sm',
    footer: 'tw:px-3 tw:py-2',
    applyButton: 'tw:h-8 tw:px-3 tw:text-sm',
    clearButton: 'tw:w-5 tw:h-5',
    icon: 'tw:[&_[data-uuidatepicker-icon]]:text-[16px]'
  },
  large: {
    input: 'tw:h-10 tw:text-base tw:px-4',
    panel: 'tw:text-base',
    header: 'tw:px-4 tw:py-2.5',
    headerButton: 'tw:w-9 tw:h-9',
    selectTrigger: 'tw:h-8 tw:pl-4 tw:pr-3 tw:text-base',
    monthSelectTrigger: 'tw:h-8 tw:pl-4 tw:pr-3 tw:text-base tw:w-[8.5em]',
    yearSelectTrigger: 'tw:h-8 tw:pl-4 tw:pr-3 tw:text-base tw:w-[5.5em]',
    selectOptions: 'tw:text-base',
    selectOption: 'tw:pl-4 tw:pr-3 tw:py-2',
    calendar: 'tw:p-4',
    weekday: 'tw:min-w-[2.75em] tw:h-[2.75em] tw:text-sm',
    day: 'tw:min-w-[2.75em] tw:h-[2.75em] tw:text-base',
    footer: 'tw:px-4 tw:py-2.5',
    applyButton: 'tw:h-9 tw:px-4 tw:text-base',
    clearButton: 'tw:w-6 tw:h-6',
    icon: 'tw:[&_[data-uuidatepicker-icon]]:text-[18px]'
  }
}

const datePickerTheme: UUIDatePickerTheme = {
  trigger: ({ size, style, invalid, disabled }) => {
    const base = `tw:flex tw:items-center tw:gap-2 tw:rounded-[20px] tw:transition-colors tw:select-none ${sizeClasses[size].input}`

    // Style-based classes (raised vs lowered)
    let styleClasses = ''
    if (disabled) {
      styleClasses = 'tw:bg-grey-800 tw:text-grey-600 tw:[&_[data-uuidatepicker-icon]]:text-grey-600 tw:cursor-default'
    } else if (style === 'lowered') {
      styleClasses = [
        'tw:text-primary-text tw:bg-grey-1000 tw:cursor-pointer',
        'tw:[&_[data-uuidatepicker-icon]]:text-secondary-text ',
        // hover
        'tw:hover:text-aquamarine-400 tw:hover:bg-grey-700 tw:hover:[&_[data-uuidatepicker-icon]]:text-secondary-text',
        // focus
        'tw:focus-within:text-blue-100 tw:focus-within:bg-grey-1000 tw:focus-within:[&_[data-uuidatepicker-icon]]:text-blue-100',
        // focus hover
        'tw:focus-within:hover:text-blue-100 tw:focus-within:hover:bg-grey-700'
      ].join(' ')
    } else {
      // raised (default)
      styleClasses = [
        'tw:text-primary-text tw:bg-aquamarine-10 tw:cursor-pointer',
        'tw:[&_[data-uuidatepicker-icon]]:text-secondary-text tw:[&_[data-uuidatepicker-icon]]:text-[16px]',
        // hover
        'tw:hover:text-aquamarine-400 tw:hover:bg-aquamarine-25 tw:hover:[&_[data-uuidatepicker-icon]]:text-secondary-text',
        // focus
        'tw:focus-within:text-blue-100 tw:focus-within:bg-aquamarine-10 tw:focus-within:[&_[data-uuidatepicker-icon]]:text-blue-100',
        // focus hover
        'tw:focus-within:hover:text-blue-100 tw:focus-within:hover:bg-aquamarine-25'
      ].join(' ')
    }

    // Invalid state override
    const invalidClasses = invalid && !disabled ? 'tw:border tw:border-red-50' : ''

    // Clear button styling
    const clearSize = {
      small: 'tw:[&_[data-uuidatepicker-clear]]:text-[16px] tw:[&_[data-uuidatepicker-clear]]:leading-[16px]',
      normal:
        'tw:[&_[data-uuidatepicker-clear]]:text-[20px] tw:[&_[data-uuidatepicker-clear]]:leading-[20px] tw:[&_[data-uuidatepicker-clear]]:-mr-1',
      large:
        'tw:[&_[data-uuidatepicker-clear]]:text-[24px] tw:[&_[data-uuidatepicker-clear]]:leading-[24px] tw:[&_[data-uuidatepicker-clear]]:-mr-2'
    }[size]

    const childStyles = [
      'tw:[&_[data-uuidatepicker-value]]:flex-1 tw:[&_[data-uuidatepicker-value]]:truncate tw:[&_[data-uuidatepicker-value]]:text-inherit tw:[&_[data-uuidatepicker-value]]:font-normal',
      // Editable input - borderless, transparent, inherits font, fills space
      'tw:[&_[data-uuidatepicker-input]]:flex-1 tw:[&_[data-uuidatepicker-input]]:bg-transparent tw:[&_[data-uuidatepicker-input]]:border-none tw:[&_[data-uuidatepicker-input]]:outline-none tw:[&_[data-uuidatepicker-input]]:text-inherit tw:[&_[data-uuidatepicker-input]]:font-inherit tw:[&_[data-uuidatepicker-input]]:min-w-0 tw:[&_[data-uuidatepicker-input]]:p-0 tw:[&_[data-uuidatepicker-input]::placeholder]:opacity-50',
      // Range separator
      'tw:[&_[data-uuidatepicker-separator]]:text-grey-500 tw:[&_[data-uuidatepicker-separator]]:flex-shrink-0 tw:[&_[data-uuidatepicker-separator]]:px-1',
      `tw:[&_[data-uuidatepicker-clear]]:transition-colors tw:[&_[data-uuidatepicker-clear]]:flex tw:[&_[data-uuidatepicker-clear]]:items-center ${clearSize}`,
      disabled
        ? 'tw:[&_[data-uuidatepicker-clear]]:cursor-default tw:[&_[data-uuidatepicker-clear]]:text-grey-500'
        : 'tw:[&_[data-uuidatepicker-clear]]:cursor-pointer tw:[&_[data-uuidatepicker-clear]]:text-grey-500 tw:[&_[data-uuidatepicker-clear]:hover]:text-primary-text'
    ].join(' ')

    return `${base} ${styleClasses} ${invalidClasses} ${childStyles}`
  },

  panel: ({ size }) => {
    const base = `tw:bg-grey-1000 tw:rounded-[20px] tw:[box-shadow:0_0_15px_4px_rgba(0,0,0,0.05)] tw:overflow-hidden tw:z-[1000] tw:select-none ${sizeClasses[size].panel}`
    const childStyles =
      'tw:[&_[data-uuidatepicker-calendars]]:flex tw:[&_[data-uuidatepicker-calendars]]:gap-4 tw:[&_[data-uuidatepicker-calendars]]:w-full'
    return `${base} tw:[min-width:var(--radix-popover-trigger-width)] ${childStyles}`
  },

  header: ({ size }) => {
    const base = `tw:flex tw:items-center tw:justify-between tw:border-b tw:border-grey-800 ${sizeClasses[size].header}`
    const childStyles = [
      'tw:[&_[data-uuidatepicker-selectors]]:flex tw:[&_[data-uuidatepicker-selectors]]:items-center tw:[&_[data-uuidatepicker-selectors]]:gap-2',
      'tw:[&_[data-uuidatepicker-dropdown-arrow]]:flex tw:[&_[data-uuidatepicker-dropdown-arrow]]:items-center tw:[&_[data-uuidatepicker-dropdown-arrow]]:text-aquamarine-400'
    ].join(' ')
    return `${base} ${childStyles}`
  },

  headerButton: {
    element: ({ size, disabled }) => {
      const base = `tw:flex tw:items-center tw:justify-center tw:rounded tw:transition-colors tw:outline-none ${sizeClasses[size].headerButton}`
      const stateClasses = disabled
        ? 'tw:text-grey-600 tw:cursor-default'
        : 'tw:text-primary-text tw:hover:bg-grey-800 tw:cursor-pointer tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      return `${base} ${stateClasses}`
    },
    label: () => 'tw:text-primary-text'
  },

  select: {
    // Raised style for header/footer selects
    trigger: ({ size, disabled, open }) => {
      const base = `tw:flex tw:items-center tw:gap-1 tw:rounded-[20px] tw:transition-colors tw:outline-none ${sizeClasses[size].selectTrigger}`
      if (disabled) {
        return `${base} tw:bg-grey-800 tw:text-grey-600 tw:cursor-default`
      }
      const focusClasses = 'tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      const stateClasses = open
        ? 'tw:text-blue-100 tw:bg-aquamarine-10 tw:[&_[data-uuidatepicker-dropdown-arrow]]:text-blue-100'
        : 'tw:text-primary-text tw:bg-aquamarine-10 tw:hover:text-aquamarine-400 tw:hover:bg-aquamarine-25 tw:cursor-pointer'
      return `${base} ${stateClasses} ${focusClasses}`
    },
    monthTrigger: ({ size, disabled, open }) => {
      const base = `tw:flex tw:items-center tw:justify-between tw:gap-1 tw:rounded-[20px] tw:transition-colors tw:outline-none ${sizeClasses[size].monthSelectTrigger}`
      if (disabled) {
        return `${base} tw:bg-grey-800 tw:text-grey-600 tw:cursor-default`
      }
      const focusClasses = 'tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      const stateClasses = open
        ? 'tw:text-blue-100 tw:bg-aquamarine-10 tw:[&_[data-uuidatepicker-dropdown-arrow]]:text-blue-100'
        : 'tw:text-primary-text tw:bg-aquamarine-10 tw:hover:text-aquamarine-400 tw:hover:bg-aquamarine-25 tw:cursor-pointer'
      return `${base} ${stateClasses} ${focusClasses}`
    },
    yearTrigger: ({ size, disabled, open }) => {
      const base = `tw:flex tw:items-center tw:justify-between tw:gap-1 tw:rounded-[20px] tw:transition-colors tw:outline-none ${sizeClasses[size].yearSelectTrigger}`
      if (disabled) {
        return `${base} tw:bg-grey-800 tw:text-grey-600 tw:cursor-default`
      }
      const focusClasses = 'tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      const stateClasses = open
        ? 'tw:text-blue-100 tw:bg-aquamarine-10 tw:[&_[data-uuidatepicker-dropdown-arrow]]:text-blue-100'
        : 'tw:text-primary-text tw:bg-aquamarine-10 tw:hover:text-aquamarine-400 tw:hover:bg-aquamarine-25 tw:cursor-pointer'
      return `${base} ${stateClasses} ${focusClasses}`
    },
    options: () =>
      'tw:bg-grey-1000 tw:rounded-[20px] tw:[box-shadow:0_0_15px_4px_rgba(0,0,0,0.05)] tw:z-[1001] tw:py-2 tw:[min-width:var(--radix-popover-trigger-width)]',
    optionsScroll: ({ size }) => {
      const scrollbar =
        'tw:[&::-webkit-scrollbar]:w-[4px] tw:[&::-webkit-scrollbar-track]:bg-transparent tw:[&::-webkit-scrollbar-thumb]:bg-grey-600 tw:[&::-webkit-scrollbar-thumb]:rounded-[24px]'
      return `tw:max-h-44 tw:overflow-y-auto ${scrollbar} ${sizeClasses[size].selectOptions}`
    },
    presetOptions: () =>
      'tw:bg-grey-1000 tw:rounded-[20px] tw:[box-shadow:0_0_15px_4px_rgba(0,0,0,0.05)] tw:z-[1001] tw:[min-width:var(--radix-popover-trigger-width)] tw:max-w-[18em] tw:py-2',
    presetOptionsScroll: ({ size }) => {
      const scrollbar =
        'tw:[&::-webkit-scrollbar]:w-[4px] tw:[&::-webkit-scrollbar-track]:bg-transparent tw:[&::-webkit-scrollbar-thumb]:bg-grey-600 tw:[&::-webkit-scrollbar-thumb]:rounded-[24px]'
      return `tw:max-h-[12em] tw:overflow-y-auto tw:break-words ${scrollbar} ${sizeClasses[size].selectOptions}`
    },
    option: ({ size, selected, disabled, highlighted }) => {
      const base = `tw:transition-colors tw:cursor-pointer tw:text-primary-text ${sizeClasses[size].selectOption}`
      const stateClasses = disabled
        ? 'tw:text-grey-600 tw:cursor-default'
        : selected
          ? 'tw:bg-aquamarine-50'
          : highlighted
            ? 'tw:bg-grey-800'
            : 'tw:hover:bg-grey-800'
      return `${base} ${stateClasses}`
    }
  },

  calendar: ({ size }) => {
    const base = `tw:flex tw:flex-col tw:w-full ${sizeClasses[size].calendar}`
    const childStyles = [
      'tw:[&_[data-uuidatepicker-month-label]]:text-center tw:[&_[data-uuidatepicker-month-label]]:font-medium tw:[&_[data-uuidatepicker-month-label]]:text-grey-30 tw:[&_[data-uuidatepicker-month-label]]:pb-1',
      'tw:[&_[data-uuidatepicker-weekdays]]:grid tw:[&_[data-uuidatepicker-weekdays]]:grid-cols-7 tw:[&_[data-uuidatepicker-weekdays]]:w-full',
      'tw:[&_[data-uuidatepicker-week]]:grid tw:[&_[data-uuidatepicker-week]]:grid-cols-7 tw:[&_[data-uuidatepicker-week]]:w-full'
    ].join(' ')
    return `${base} ${childStyles}`
  },

  weekday: ({ size }) =>
    `tw:flex tw:items-center tw:justify-center tw:text-grey-10 tw:font-medium ${sizeClasses[size].weekday}`,

  day: ({
    size,
    today,
    selected,
    inRange,
    disabled,
    focused,
    isRangeStart,
    isRangeEnd,
    outsideMonth,
    isHoverPreview
  }) => {
    // Base without rounded - we add it conditionally to avoid dotted pattern in ranges
    const base = `tw:flex tw:items-center tw:justify-center tw:border ${sizeClasses[size].day}`
    // Keyboard focus - very visible
    const focusStyles = focused ? 'tw:!bg-grey-800 tw:!text-primary-text' : ''
    // Only round corners for non-range-middle cells
    const rounded = 'tw:rounded'

    if (disabled) {
      return `${base} ${rounded} tw:border-transparent tw:text-grey-600 tw:cursor-default ${focusStyles}`
    }

    // Outside month days - muted
    if (outsideMonth) {
      if (selected || isRangeStart || isRangeEnd) {
        return `${base} ${rounded} tw:border-transparent tw:bg-blue-25/80 tw:text-primary-text/70 tw:cursor-pointer ${focusStyles}`
      }
      if (inRange || isHoverPreview) {
        const bgOpacity = isHoverPreview ? 'tw:bg-blue-25/20' : 'tw:bg-blue-25'
        // No rounded for range middle cells
        return `${base} tw:border-transparent ${bgOpacity} tw:text-grey-600 tw:cursor-pointer ${focusStyles}`
      }
      return `${base} ${rounded} tw:border-transparent tw:text-grey-600 tw:cursor-pointer tw:hover:bg-grey-800/50 ${focusStyles}`
    }

    if (selected || isRangeStart || isRangeEnd) {
      return `${base} ${rounded} tw:border-transparent tw:bg-blue-100 tw:text-grey-1000 tw:cursor-pointer ${focusStyles}`
    }

    if (inRange || isHoverPreview) {
      const bgOpacity = isHoverPreview ? 'tw:bg-blue-25/80' : 'tw:bg-blue-25'
      // No rounded for range middle cells
      return `${base} tw:border-transparent ${bgOpacity} tw:text-primary-text tw:cursor-pointer ${focusStyles}`
    }

    if (today) {
      return `${base} ${rounded} tw:border-blue-100 tw:text-blue-100 tw:cursor-pointer tw:hover:bg-grey-800 ${focusStyles}`
    }

    return `${base} ${rounded} tw:border-transparent tw:text-primary-text tw:cursor-pointer tw:hover:bg-grey-800 ${focusStyles}`
  },

  footer: ({ size }) => {
    const base = `tw:flex tw:flex-nowrap tw:items-center tw:justify-between tw:border-t tw:border-grey-800 ${sizeClasses[size].footer}`
    const childStyles = [
      'tw:[&_[data-uuidatepicker-footer-left]]:flex tw:[&_[data-uuidatepicker-footer-left]]:flex-nowrap tw:[&_[data-uuidatepicker-footer-left]]:items-center tw:[&_[data-uuidatepicker-footer-left]]:gap-2',
      'tw:[&_[data-uuidatepicker-footer-right]]:flex tw:[&_[data-uuidatepicker-footer-right]]:flex-nowrap tw:[&_[data-uuidatepicker-footer-right]]:items-center tw:[&_[data-uuidatepicker-footer-right]]:gap-2',
      'tw:[&_[data-uuidatepicker-dropdown-arrow]]:flex tw:[&_[data-uuidatepicker-dropdown-arrow]]:items-center tw:[&_[data-uuidatepicker-dropdown-arrow]]:text-aquamarine-400'
    ].join(' ')
    return `${base} ${childStyles}`
  },

  applyButton: {
    element: ({ size, disabled }) => {
      const base = `tw:flex tw:items-center tw:justify-center tw:rounded-[20px] tw:bg-blue-100 tw:text-grey-1000 tw:font-medium tw:transition-colors tw:outline-none ${sizeClasses[size].applyButton}`
      const stateClasses = disabled
        ? 'tw:opacity-50 tw:cursor-default'
        : 'tw:hover:bg-blue-300 tw:cursor-pointer tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      return `${base} ${stateClasses}`
    },
    label: () => ''
  },

  clearButton: {
    element: ({ size, disabled }) => {
      const base = `tw:flex tw:items-center tw:justify-center tw:rounded-[20px] tw:bg-grey-800 tw:text-primary-text tw:transition-colors tw:outline-none tw:mr-[1em] ${sizeClasses[size].applyButton}`
      const stateClasses = disabled
        ? 'tw:opacity-50 tw:cursor-default'
        : 'tw:hover:bg-grey-700  tw:cursor-pointer tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      return `${base} ${stateClasses}`
    },
    label: () => ''
  },

  cancelButton: {
    element: ({ size, disabled }) => {
      const base = `tw:flex tw:items-center tw:justify-center tw:rounded-[20px] tw:bg-grey-800 tw:text-primary-text tw:transition-colors tw:outline-none ${sizeClasses[size].applyButton}`
      const stateClasses = disabled
        ? 'tw:opacity-50 tw:cursor-default'
        : 'tw:hover:bg-grey-700  tw:cursor-pointer tw:focus-visible:ring-2 tw:focus-visible:ring-blue-100'
      return `${base} ${stateClasses}`
    },
    label: () => ''
  }
}

export default datePickerTheme
